import { defineStore } from 'pinia';
import http from './http';
import { Response } from '@/types';
import { useMenuStore } from './menu';
import { useLoadingStore } from './loading';
import { proxyApi } from '@/utils/file';
import CryptoJS from 'crypto-js';
import { useAuthStore } from '@/plugins';

export interface Account {
  id: string;
  nickname: string;
  username: string;
  avatar: string | null;
  token: string | null;
  token_expire_time: string | null;
  is_admin: number;
}

export type TokenResult = {
  token: string;
  expires: number;
};
export const useAccountStore = defineStore('account', {
  state() {
    return {
      account: {} as Account,
      permissions: [] as string[],
      role: '',
      logged: true,
      /** 是否为登录页登陆成功跳转到首页 */
      isLoginToHome: false,
    };
  },
  actions: {
    /**
     * 登录
     * @param username 账号
     * @param password 密码
     */
    async login(username: string, password: string) {
      return http
        .request<any, Response<any>>(proxyApi() + '/admin/login', 'post_json', { username, password })
        .then(async (response) => {
          if (response.code === 200) {
            this.logged = true;
            this.setAccount(response.data.user_info);
            this.role = '';
            // 暂时通过此方法去掉token
            http.removeAuthorization();
            // http.setAuthorization(`Bearer ${response.data.token}`, new Date(response.data.expires));
            await useMenuStore().getMenuList();
            return true;
          } else {
            return false;
          }
        });
    },
    /** 清除用户数据 */
    clearAccount() {
      this.account = {} as Account;
      localStorage.removeItem(import.meta.env.VITE_BASE_DATA);
      localStorage.removeItem('stepin-menu');
      http.removeAuthorization();
      this.logged = false;
    },
    /** 退出登录 */
    async logout() {
      return http.request<null, Response<boolean>>(proxyApi() + '/admin/logout', 'get').then((response) => {
        if (response.code === 200) {
          this.clearAccount();
          return true;
        } else {
          return Promise.reject(response);
        }
      });
    },
    async profile() {
      const { setAuthLoading } = useLoadingStore();
      setAuthLoading(true);
      return http
        .request<null, Response<Account>>(proxyApi() + '/admin/user/info', 'get')
        .then(async (response) => {
          if (response.code === 200) {
            this.setAccount(response.data);
            this.role = '';
            let auths = [];
            if (response.data?.is_admin === 1) {
              auths = ['is_admin'];
            }
            useAuthStore().setAuthorities(auths);
            useMenuStore().checkMenuPermission();
            return response.data;
          } else {
            return Promise.reject(response);
          }
        })
        .finally(() => setAuthLoading(false));
    },
    setLogged(logged: boolean) {
      this.logged = logged;
    },
    setIsLoginToHome(isLoginToHome: boolean) {
      this.isLoginToHome = isLoginToHome;
    },
    setAccount(account: Account) {
      try {
        this.account = account;
        const key = import.meta.env.VITE_BASE_DATA;
        const result = CryptoJS.AES.encrypt(JSON.stringify(account), key).toString();
        localStorage.setItem(key, result);
      } catch (error) {
        console.log('setAccount-error: ', error);
      }
    },
    getAccount(): Account {
      if (!this.account.id) {
        try {
          const key = import.meta.env.VITE_BASE_DATA;
          const encryptedText = localStorage.getItem(key);
          if (encryptedText) {
            const result = CryptoJS.AES.decrypt(encryptedText, key).toString(CryptoJS.enc.Utf8);
            this.account = JSON.parse(result);
          }
          this.account = this.account ? this.account : ({} as Account);
        } catch (error) {
          console.error('getAccount-error: ', error);
        }
      }
      return this.account;
    },
  },
});
